import classNames from 'classnames';
import { useIsMounted } from '~/hooks';
import { rgba } from 'polished';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { colors } from '~/styles';
import Button from './Button';
import { Container } from './Dropdown';
import DropdownDeleteItem from './DropdownDeleteItem';
import InlineTooltip from './InlineTooltip';
import { useScrollVisibility } from './popperUtils';

const Content = styled.div`
  padding: 0.5rem 0;
  background-color: ${colors.white};
  border-radius: 0.25rem;
  box-shadow:
    0 0.5em 1em -0.125em ${rgba(colors.black, 0.1)},
    0 0 0 1px ${rgba(colors.black, 0.02)};
`;

const PopperMenu = styled.div`
  z-index: 1000;

  &.scrollable > ${Content} {
    max-height: 15rem;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

const itemStyle = css`
  display: flex;
  align-items: center;
  padding: 0.25rem 1.25rem;
  border-radius: 0;
  width: 100%;
  justify-content: flex-start;
  color: ${colors.black};
  text-align: left;
  position: relative;

  &:hover {
    background-color: ${colors.grey5};
    color: ${colors.black};
  }
`;

const ItemButton = styled(Button)`
  ${itemStyle}

  &:disabled {
    cursor: not-allowed;

    &&&:hover {
      background: none;
      font-weight: normal;
      color: ${colors.black};
    }
  }
`;

const ItemLink = styled(Link)`
  ${itemStyle}
`;

const ItemText = styled.span`
  display: flex;
  align-items: center;
  padding: 0.25rem 1.25rem;
  border-radius: 0;
  width: 100%;
  justify-content: flex-start;
  color: ${colors.grey55};
  cursor: default;
`;

const FixedDropdownContext = React.createContext();

function FixedDropdown({ children, align = 'left', position = 'auto', className, onClose, ...props }) {
  const [isOpen, setIsOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const containerRef = React.useRef();
  const isMounted = useIsMounted();

  // Click-outside detection that accounts for both container and portaled popper
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      const clickedInContainer = containerRef.current?.contains(event.target);
      const clickedInPopper = popperElement?.contains(event.target);

      if (!clickedInContainer && !clickedInPopper) {
        if (props.isOpen === undefined) {
          setIsOpen(false);
        } else {
          onClose && onClose(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, props.isOpen, onClose, popperElement]);

  useEffect(() => {
    setIsOpen(props.isOpen);
  }, [props.isOpen]);

  const handleSetIsOpen = (flag) => {
    if (!isMounted.current) return;
    setIsOpen(!!flag);
  };

  const setContainerRef = useCallback(
    (node) => {
      containerRef.current = node;
      setReferenceElement(node);
    },
    [setReferenceElement],
  );

  return (
    <FixedDropdownContext.Provider
      value={{ align, position, isOpen, referenceElement, popperElement, setPopperElement }}>
      <Container ref={setContainerRef} className={className} {...props}>
        {typeof children === 'function' ? children({ setIsOpen: handleSetIsOpen, isOpen }) : children}
      </Container>
    </FixedDropdownContext.Provider>
  );
}

function FixedDropdownMenu({ children, className, scrollable, ...props }) {
  const {
    align,
    position: defaultMenuPosition,
    isOpen,
    referenceElement,
    setPopperElement,
  } = useContext(FixedDropdownContext);

  const [popperElement, setLocalPopperElement] = useState(null);

  // Sync popper element to context for click-outside detection
  const setPopperRef = useCallback(
    (node) => {
      setLocalPopperElement(node);
      setPopperElement(node);
    },
    [setPopperElement],
  );

  const popperPlacement = (() => {
    const vertical = defaultMenuPosition === 'top' ? 'top' : 'bottom';
    const horizontal = align === 'right' ? 'end' : 'start';
    return `${vertical}-${horizontal}`;
  })();

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: popperPlacement,
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 4] } },
      { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-start'] } },
    ],
  });

  const isVisible = useScrollVisibility(referenceElement, isOpen);

  if (!isOpen || !isVisible) return null;

  return ReactDOM.createPortal(
    <PopperMenu
      ref={setPopperRef}
      className={classNames(className, { scrollable })}
      style={{ ...styles.popper, minWidth: referenceElement?.offsetWidth }}
      {...attributes.popper}
      {...props}>
      <Content>{children}</Content>
    </PopperMenu>,
    document.body,
  );
}

// =============================================================================
// Sub-components (same API as Dropdown)
// =============================================================================

const FixedDropdownTrigger = styled.div``;

function FixedDropdownItem({ tooltip, tooltipPlacement, children, ...props }) {
  return (
    <ItemButton isAnchor {...props} onClick={props.disabled ? undefined : props.onClick}>
      {children}
      {tooltip && <InlineTooltip message={tooltip} placement={tooltipPlacement} />}
    </ItemButton>
  );
}

function FixedDropdownLink(props) {
  return <ItemLink {...props} />;
}

function FixedDropdownText(props) {
  return <ItemText {...props} />;
}

const FixedDropdownPanel = styled.div`
  padding: 0.5rem;
`;

FixedDropdown.Trigger = FixedDropdownTrigger;
FixedDropdown.Menu = FixedDropdownMenu;
FixedDropdown.Item = FixedDropdownItem;
FixedDropdown.DeleteItem = DropdownDeleteItem;
FixedDropdown.Link = FixedDropdownLink;
FixedDropdown.Text = FixedDropdownText;
FixedDropdown.Panel = FixedDropdownPanel;

export default FixedDropdown;
export { FixedDropdown, FixedDropdownMenu, FixedDropdownItem, FixedDropdownLink, FixedDropdownText };
